/**
 * EstimateDetailModal - Modal for viewing estimate details with actions
 */

import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import {
  CheckCircle,
  PaperPlaneTilt,
  Briefcase,
  Receipt,
  Trash,
  PencilSimple,
  DownloadSimple,
  Printer,
  SpinnerGap,
  X,
} from "phosphor-react";
import { toast } from "sonner";
import { useState } from "react";
import { formatCurrency } from "../../utils/formatters";
import { 
  canConvertToJob, 
  canSendToCustomer, 
  canApprove,
  canReject,
  getStatusColor as getStatusColorUtil 
} from "../../utils/estimateStatus";
import DepositStatusLine from "./DepositStatusLine";
import MarkDepositReceivedModal from "./MarkDepositReceivedModal";
// Intentionally hidden in authenticated record modals only; public pages keep branding.
// import PoweredByFSPFooter from "../PoweredByFSPFooter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EstimateDetailModal({
  isOpen,
  onOpenChange,
  estimate,
  onApprove,
  onReject,
  onConvertToJob,
  onConvertToInvoice,
  onSend,
  onEdit,
  onDelete,
  onDepositMarkedReceived,
  paymentsEnabled = false,
  getStatusColor: getStatusColorProp,
}) {
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [approvingStatus, setApprovingStatus] = useState(false);
  const [rejectingStatus, setRejectingStatus] = useState(false);
  const [showMarkDepositModal, setShowMarkDepositModal] = useState(false);
  const [depositInvoice, setDepositInvoice] = useState(null);
  
  // Use provided getStatusColor or fall back to utility
  const getStatusColor = getStatusColorProp || getStatusColorUtil;
  
  if (!estimate) return null;

  // Handle mark deposit received - works for both Option 1 (INV) and Option 2 (DEP)
  const handleMarkDepositReceived = async () => {
    // Option 1: Check for linked invoice (invoice_id)
    // Option 2: Check for legacy deposit invoice (deposit_invoice_id)
    const hasInvoice = estimate.invoice_id || estimate.deposit_invoice_id;
    
    if (!hasInvoice) {
      toast.error("No invoice found. Please approve the estimate first.");
      return;
    }
    
    // For Option 2 (legacy DEP invoices), we still need to fetch the invoice details
    // For Option 1, we can proceed directly to the modal with estimate data
    if (estimate.deposit_invoice_id) {
      // Legacy Option 2 flow - fetch DEP invoice
      try {
        const response = await fetch(`${API}/invoices/${estimate.deposit_invoice_id}`, {
          credentials: "include",
        });
        if (response.ok) {
          const invoice = await response.json();
          setDepositInvoice(invoice);
          setShowMarkDepositModal(true);
        } else {
          toast.error("Failed to load deposit invoice");
        }
      } catch (error) {
        console.error("Error fetching deposit invoice:", error);
        toast.error("Failed to load deposit invoice");
      }
    } else if (estimate.invoice_id) {
      // Option 1 flow - use linked INV invoice
      try {
        const response = await fetch(`${API}/invoices/${estimate.invoice_id}`, {
          credentials: "include",
        });
        if (response.ok) {
          const invoice = await response.json();
          // For Option 1, set depositInvoice to the INV invoice with deposit amount
          const depositAmount = invoice.deposit_amount_cents 
            ? invoice.deposit_amount_cents / 100 
            : (estimate.deposit_value && estimate.deposit_type === 'PERCENT' 
                ? (estimate.total * estimate.deposit_value / 100) 
                : estimate.deposit_value || 0);
          setDepositInvoice({
            ...invoice,
            total: depositAmount, // Show deposit amount in modal
            isOption1: true, // Flag for modal to use new endpoint
          });
          setShowMarkDepositModal(true);
        } else {
          toast.error("Failed to load invoice");
        }
      } catch (error) {
        console.error("Error fetching invoice:", error);
        toast.error("Failed to load invoice");
      }
    }
  };

  // Handle deposit marked received success
  const handleDepositMarkedSuccess = () => {
    setShowMarkDepositModal(false);
    setDepositInvoice(null);
    if (onDepositMarkedReceived) {
      onDepositMarkedReceived();
    }
  };

  // Handle approve with loading state
  const handleApprove = async () => {
    if (approvingStatus || rejectingStatus) return;
    setApprovingStatus(true);
    try {
      await onApprove(estimate.estimate_id);
    } finally {
      setApprovingStatus(false);
    }
  };

  // Handle reject with loading state  
  const handleReject = async () => {
    if (approvingStatus || rejectingStatus) return;
    setRejectingStatus(true);
    try {
      await onReject(estimate.estimate_id);
    } finally {
      setRejectingStatus(false);
    }
  };

  // Download PDF - get a temporary token then download via fetch
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      console.log("[PDF] Starting download for estimate:", estimate.estimate_id);
      
      // Get auth token for requests
      const authToken = localStorage.getItem('auth_token');
      const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      // Get a temporary download token
      const tokenResponse = await fetch(`${API}/estimates/${estimate.estimate_id}/pdf-token`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders,
      });
      
      console.log("[PDF] Token response status:", tokenResponse.status);
      
      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error("[PDF] Token error:", errorText);
        throw new Error("Failed to get download token");
      }
      
      const { token } = await tokenResponse.json();
      console.log("[PDF] Got token:", token?.substring(0, 20) + "...");
      
      // Build the PDF URL
      const pdfUrl = `${API}/estimates/${estimate.estimate_id}/pdf?token=${token}`;
      console.log("[PDF] Fetching PDF from:", pdfUrl);
      
      // Fetch the PDF as a blob - this bypasses popup blockers
      const pdfResponse = await fetch(pdfUrl, {
        method: "GET",
        credentials: "include",
        headers: authHeaders,
      });
      
      if (!pdfResponse.ok) {
        throw new Error(`PDF download failed: ${pdfResponse.status}`);
      }
      
      const blob = await pdfResponse.blob();
      console.log("[PDF] Got blob, size:", blob.size, "type:", blob.type);
      
      // Validate blob is actually a PDF
      if (blob.size < 500 || !blob.type.includes('pdf')) {
        console.error("[PDF] Invalid blob - size:", blob.size, "type:", blob.type);
        throw new Error("Invalid PDF response");
      }
      
      // Create a download link and trigger it
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Estimate_${estimate.estimate_number || estimate.estimate_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 1000);
      
      toast.success("PDF downloaded successfully");
      console.log("[PDF] Download complete");
      
    } catch (error) {
      console.error("[PDF] Download error:", error);
      toast.error("Failed to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Print PDF - fetch blob and open print dialog
  const handlePrintPdf = async () => {
    setPrinting(true);
    try {
      // Get auth token for requests
      const authToken = localStorage.getItem('auth_token');
      const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      // Get a temporary download token
      const tokenResponse = await fetch(`${API}/estimates/${estimate.estimate_id}/pdf-token`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders,
      });
      
      if (!tokenResponse.ok) {
        throw new Error("Failed to get print token");
      }
      
      const { token } = await tokenResponse.json();
      const pdfUrl = `${API}/estimates/${estimate.estimate_id}/pdf?token=${token}`;
      
      // Fetch the PDF as a blob
      const pdfResponse = await fetch(pdfUrl, {
        method: "GET",
        credentials: "include",
        headers: authHeaders,
      });
      
      if (!pdfResponse.ok) {
        throw new Error(`PDF fetch failed: ${pdfResponse.status}`);
      }
      
      const blob = await pdfResponse.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a hidden iframe for printing
      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "none";
      printFrame.src = blobUrl;
      
      document.body.appendChild(printFrame);
      
      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.print();
          // Clean up after print dialog closes
          setTimeout(() => {
            document.body.removeChild(printFrame);
            window.URL.revokeObjectURL(blobUrl);
          }, 1000);
        }, 500);
      };
      
      toast.success("Opening print dialog...");
      
    } catch (error) {
      console.error("[Print] Error:", error);
      toast.error("Failed to print. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="estimate-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            {estimate.estimate_number}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-lg">{estimate.title}</h3>
                <p className="text-sm text-zinc-500">{estimate.customer?.name}</p>
                {estimate.customer?.email && (
                  <p className="text-xs text-zinc-400">{estimate.customer.email}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {canSendToCustomer(estimate.status) && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onEdit}
                      className="h-8 w-8 text-zinc-600 hover:text-[#FF6600] hover:bg-[#FF6600]/10"
                      data-testid="edit-estimate-btn"
                  >
                    <PencilSimple size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onDelete}
                    className="h-8 w-8 text-zinc-600 hover:text-red-600 hover:bg-red-50"
                    data-testid="delete-estimate-btn"
                  >
                    <Trash size={18} />
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="h-8 w-8 text-zinc-600 hover:text-blue-600 hover:bg-blue-50"
                data-testid="download-estimate-pdf-btn"
                title="Download PDF"
              >
                {downloading ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <DownloadSimple size={18} />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrintPdf}
                disabled={printing}
                className="h-8 w-8 text-zinc-600 hover:text-green-600 hover:bg-green-50"
                data-testid="print-estimate-pdf-btn"
                title="Print"
              >
                {printing ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <Printer size={18} />
                )}
              </Button>
              <Badge variant="outline" className={getStatusColor(estimate.status)}>
                {estimate.status}
              </Badge>
            </div>
          </div>

          {/* Dates - matching Invoice modal layout */}
          <div className="flex flex-wrap gap-4 sm:gap-6 text-sm">
            <div>
              <span className="text-zinc-400">Created: </span>
              <span>{estimate.created_at ? new Date(estimate.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span>
            </div>
            {estimate.sent_at && (
              <div>
                <span className="text-zinc-400">Sent: </span>
                <span>{new Date(estimate.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}
            {estimate.valid_until && (
              <div>
                <span className="text-zinc-400">Valid Until: </span>
                <span className={new Date(estimate.valid_until) < new Date() ? "text-amber-600 font-medium" : ""}>
                  {new Date(estimate.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="bg-zinc-50 p-3 text-xs font-medium text-zinc-500 uppercase grid grid-cols-12 gap-4">
              <div className="col-span-5">Description</div>
              <div className="col-span-2 text-center">Qty</div>
              <div className="col-span-2 text-right">Price</div>
              <div className="col-span-1 text-center">Tax</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {estimate.items?.map((item, idx) => (
              <div key={idx} className="p-3 border-t border-zinc-100 grid grid-cols-12 gap-4 text-sm">
                <div className="col-span-5">{item.description || "Item"}</div>
                <div className="col-span-2 text-center">{item.quantity}</div>
                <div className="col-span-2 text-right">{formatCurrency(item.price)}</div>
                <div className="col-span-1 text-center">
                  {item.is_taxable && <CheckCircle size={16} className="text-emerald-500 mx-auto" />}
                </div>
                <div className="col-span-2 text-right font-medium">
                  {formatCurrency(item.quantity * item.price)}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="bg-zinc-50 p-4 rounded-sm space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{formatCurrency(estimate.subtotal)}</span>
            </div>
            {(estimate.tax > 0 || estimate.tax_rate_percent > 0) && (
              <div className="flex justify-between text-sm">
                <span>
                  Tax ({estimate.tax_rate_percent || 0}%
                  {estimate.taxable_amount > 0 && ` on ${formatCurrency(estimate.taxable_amount)}`})
                </span>
                <span>{formatCurrency(estimate.tax)}</span>
              </div>
            )}
            <div className="flex justify-between font-heading text-xl font-bold border-t border-zinc-200 pt-2">
              <span>Total</span>
              <span>{formatCurrency(estimate.total)}</span>
            </div>
          </div>

          {/* Deposit Status - show only if deposit is enabled */}
          {estimate.deposit_enabled && (
            <DepositStatusLine
              estimate={estimate}
              showMarkReceivedAction={estimate.deposit_status === "REQUESTED" && estimate.deposit_paid !== true}
              onMarkReceived={handleMarkDepositReceived}
            />
          )}

          {estimate.notes && (
            <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-md">
              <p className="text-xs text-zinc-400 uppercase mb-1">Notes</p>
              <p className="text-sm">{estimate.notes}</p>
            </div>
          )}
          
          {/* Intentionally hidden in authenticated record modals only; public pages keep branding. */}
          </div>
        </DialogBody>

        {/* Actions Footer */}
        <DialogFooter className="flex-wrap gap-2">
          {/* Draft: Show Reject + Approve + Send buttons */}
          {estimate.status === "draft" && (
            <>
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={rejectingStatus || approvingStatus}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="reject-estimate-btn"
              >
                {rejectingStatus ? (
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                ) : (
                  <X size={18} className="mr-2" />
                )}
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={approvingStatus || rejectingStatus}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="approve-estimate-btn"
              >
                {approvingStatus ? (
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={18} className="mr-2" />
                )}
                Approve
              </Button>
              <Button 
                variant="outline" 
                className="border-2"
                onClick={() => onSend && onSend(estimate)}
                data-testid="send-estimate-btn"
              >
                <PaperPlaneTilt size={18} className="mr-2" />
                Send to Customer
              </Button>
            </>
          )}
          {/* Sent: Show Reject, Approve buttons on LEFT, Resend on RIGHT */}
          {estimate.status === "sent" && (
            <>
              {/* Reject button - uses existing red status color */}
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={rejectingStatus || approvingStatus}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="reject-estimate-btn"
              >
                {rejectingStatus ? (
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                ) : (
                  <X size={18} className="mr-2" />
                )}
                Reject
              </Button>
              {/* Approve button - uses existing emerald status color */}
              <Button
                onClick={handleApprove}
                disabled={approvingStatus || rejectingStatus}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="approve-estimate-btn"
              >
                {approvingStatus ? (
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={18} className="mr-2" />
                )}
                Approve
              </Button>
              {/* Resend stays on right */}
              <Button 
                variant="outline" 
                className="border-2"
                onClick={() => onSend && onSend(estimate)}
                data-testid="resend-estimate-btn"
              >
                <PaperPlaneTilt size={18} className="mr-2" />
                Resend to Customer
              </Button>
            </>
          )}
          {/* Rejected: Show indicator that it's rejected, allow re-approval */}
          {estimate.status === "rejected" && (
            <>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 px-3 py-1.5">
                <X size={16} className="mr-1" />
                Rejected
              </Badge>
              <Button
                onClick={handleApprove}
                disabled={approvingStatus}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="approve-estimate-btn"
              >
                {approvingStatus ? (
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={18} className="mr-2" />
                )}
                Approve Instead
              </Button>
            </>
          )}
          {canConvertToJob(estimate) && (
            <>
              <Button
                onClick={() => onConvertToJob(estimate.estimate_id)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="convert-to-job-btn"
              >
                <Briefcase size={18} className="mr-2" />
                Convert to Job
              </Button>
              <Button
                onClick={() => onConvertToInvoice(estimate.estimate_id)}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="convert-to-invoice-btn"
              >
                <Receipt size={18} className="mr-2" />
                Convert to Invoice
              </Button>
            </>
          )}
          {estimate.job_id && (
            <Link to={`/jobs/${estimate.job_id}`}>
              <Button variant="outline" className="border-2" data-testid="view-job-btn">
                <Briefcase size={18} className="mr-2" />
                View Job
              </Button>
            </Link>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Mark Deposit Received Modal (for offline payments) */}
      <MarkDepositReceivedModal
        isOpen={showMarkDepositModal}
        onOpenChange={setShowMarkDepositModal}
        depositInvoice={depositInvoice}
        onSuccess={handleDepositMarkedSuccess}
      />
    </Dialog>
  );
}
